/* eslint-disable import/no-cycle */
import { createAsyncThunk } from '@reduxjs/toolkit'
import authenticationAPI from 'services/authentication'
import { LoginParams } from 'typings/authentication'
import { AuthenticateResponse } from 'typings/apiRequest'
import { showWelcomeMat } from 'redux/appInfo/appInfoSlice'
import type { OriginalRootState } from 'redux/rootReducer'
import {
  loginError,
  selectAccessToken,
  tokenRequestSuccess
} from 'redux/authToken/authTokenSlice'
import { UserInfoRequest } from 'services/authentication/types'
import { loginSuccessful } from 'redux/demographic/demographicSlice'
import { ActiveAccount } from 'typings/demographic'
import authTokenAPI from 'services/authToken'
import { isServerError } from 'helpers/parseError'
import { ServerError } from 'typings/serverError'
import {
  contactInfoUpdatePending,
  contactInfoUpdated,
  resetUpdateStatus,
  selectAccountId,
  selectUserID,
  selectUserInfo,
  setUserInfo
} from './loginInfoSlice'
import { UpdateMFAContactInfoAction, UserInfoState } from './typings'

const CIPHER_PARTIAL_ACCOUNT_ID = 'cipher.partialAccountId'

/**
 * Fetches logged in user info from api. And dispatches showWelcomeMat if the
 * contact update required flag is false.
 */
export const getUserInfo = createAsyncThunk<
  Promise<void>,
  void,
  {
    state: OriginalRootState
  }
>('authenticationSlice/getUserInfo', async (_, { dispatch, getState }) => {
  try {
    const state = getState()
    const accessToken = selectAccessToken(state)
    const userID = selectUserID(state)
    const accountId = selectAccountId(state)

    const requestBody: UserInfoRequest = {
      accessToken,
      userID,
      'cipher.accountId': accountId
    }

    const response = await authenticationAPI.getUserInfo(requestBody)
    const {
      firstName,
      lastName,
      emailAddress,
      phoneNumber,
      contactUpdateRequired
    } = response

    const userInfo: UserInfoState = {
      firstName: firstName || '',
      lastName: lastName || '',
      emailAddress: emailAddress || '',
      phoneNumber: phoneNumber || '',
      hasUserInfoResponseSuccess: true,
      contactUpdateRequired
    }

    const { hasUserInfoResponseSuccess } = selectUserInfo(state)

    /*
     * shows the welcome mat immediately if the user does not need to update
     * contact info and getting the user info is part of the login flow
     */

    if (!contactUpdateRequired && !hasUserInfoResponseSuccess) {
      dispatch(showWelcomeMat())
    }

    dispatch(setUserInfo(userInfo))
  } catch (error) {
    dispatch(
      setUserInfo({
        firstName: '',
        lastName: '',
        emailAddress: '',
        phoneNumber: '',
        hasUserInfoResponseSuccess: false,
        contactUpdateRequired: false
      })
    )
  }
})

/**
 * This action handles the submission of the Update Contact Information form
 * presented in Edit User Information
 */
export const updateProfileInfo = createAsyncThunk<
  Promise<void>,
  UpdateMFAContactInfoAction,
  {
    state: OriginalRootState
  }
>(
  'authenticationSlice/updateMfaContactInfo',
  async (payload, { dispatch, getState }) => {
    try {
      dispatch(resetUpdateStatus())

      dispatch(contactInfoUpdatePending())

      const state = getState()
      const accessToken = selectAccessToken(state)
      const userID = selectUserID(state)

      const { emailAddress, phoneNumber } = payload

      const response = await authenticationAPI.updateContactInfo({
        emailAddress,
        phoneNumber,
        accessToken,
        userID
      })

      if (response) {
        await dispatch(getUserInfo())
        dispatch(contactInfoUpdated())
      }
    } catch (error) {
      dispatch(resetUpdateStatus())
    }
  }
)

/**
 * This action handles the submission of the MFA Contact Info Modal that is
 * displayed when the user first logs in. It handles both the "My Info is
 * Correct" as well as the update actions of the form.
 */
export const mfaContactModalSubmitted = createAsyncThunk<
  Promise<void>,
  UpdateMFAContactInfoAction,
  {
    state: OriginalRootState
  }
>(
  'authenticationSlice/mfaContactModalSubmitted',
  async (payload, { dispatch, getState }) => {
    const state = getState()
    const accessToken = selectAccessToken(state)
    const userID = selectUserID(state)

    await authenticationAPI.updateContactInfo({
      ...payload,
      accessToken,
      userID,
      /*
       * set noRedirect to true because requirement is to close the modal and
       * ignore response from api. This prevents the request from redirecting to
       * the error page if api responds with error.
       */
      noRedirect: true
    })

    await dispatch(getUserInfo())
  }
)

/*
 * This thunk handles the initial authentication flow from the Login Form
 * Submission.
 *
 * Flow begins by sending message with user ID and pw to the authenticate api. The
 * authenticate API response includes the firstTimelogon flag which indicates what
 * must happen next. The flow branches to continue the typical login flow
 * (firstTimelogon = "N") or continues to fetch an auth token for other flows.
 * (firstTimelogn <> "N").
 */
export const loginSubmitted = createAsyncThunk<
  AuthenticateResponse,
  LoginParams,
  { rejectValue: ServerError }
  // eslint-disable-next-line consistent-return
>(
  'loginInfoSlice/loginSubmitted',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const authenticateResponse = await authenticationAPI.authenticate(payload)

      const { accountType, authCode } = authenticateResponse
      const partialAccountId = authenticateResponse[CIPHER_PARTIAL_ACCOUNT_ID]
      const accountID = authenticateResponse['cipher.accountId']

      const accountNumber =
        accountType === 'prox' ? partialAccountId : accountID

      const activeAccount: ActiveAccount = {
        accountID,
        accountNumber
      }
      dispatch(loginSuccessful(activeAccount))

      // Need to fetch auth token for Reset password and First Time Login flows (flag R | C | O | U)
      if (authenticateResponse.firstTimeLogon !== 'N') {
        const tokenResponse = await authTokenAPI.getAuthToken({ authCode })
        dispatch(tokenRequestSuccess(tokenResponse))
      }

      return authenticateResponse
    } catch (error) {
      dispatch(loginError())

      if (isServerError(error)) {
        return rejectWithValue(error)
      }

      // this is an unexpected error (TODO: validate that this will render the System Error Page)
      throw error
    }
  }
)
